import { TargetTagType } from '@/common/utils';
import {
  WidgetGroupValueResponseV8,
  WidgetValueTargetDetailV7,
  WidgetValueTargetV7,
} from '@/api-client/metav8Over/model';
import { LabelFormat } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/prometheus/chartDataSettingsItem.types';
import { GridLayoutItem, WidgetChartData } from '@/dashboard/shared/types/widgets.types';
import { DataResource } from '@/dashboard/features/editToolbox/types/dataResource.types';

export const DEFAULT_DATA_RESOURCE: DataResource = 'default';

export const dataResourceMap: { [key in string]: GridLayoutItem['dataResource'] } = {
  exemOne: DEFAULT_DATA_RESOURCE, // NOTE: 기존 exemOne으로 저장된 데이터 변경을 위한 임시 값
  default: DEFAULT_DATA_RESOURCE,
  prometheus: 'prometheus',
  elasticsearch: 'elasticsearch',
};

export const labelFormatTypeMap: { [key in string]: LabelFormat } = {
  labelKey: 'labelKey',
  custom: 'custom',
};

export const widgetTypeMap: {
  [key in Exclude<
    WidgetGroupValueResponseV8['widgetType'],
    undefined
  >]: GridLayoutItem['widgetType'];
} = {
  GRAPHS: 'GRAPHS',
  ARCHITECTURE: 'ARCHITECTURE',
  LIST: 'LIST',
  MEMO: 'MEMO',
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  GROUP: 'GROUP',
  SERVICE: 'SERVICE',
  ETC: 'ETC',
  FILTER: 'FILTER',
  ALERT: 'ALERT',
};

export const chartTypeMap: {
  [key in Exclude<WidgetGroupValueResponseV8['chartType'], undefined>]: GridLayoutItem['chartType'];
} = {
  TIME_SERIES: 'TIME_SERIES',
  SCATTER: 'SCATTER',
  ONE_DAY_COMPARE: 'ONE_DAY_COMPARE',
  EQUALIZER: 'EQUALIZER',
  SCOREBOARD: 'SCOREBOARD',
  STACKED_PROPORTION_BAR: 'STACKED_PROPORTION_BAR',
  ACTION_VIEW: 'ACTION_VIEW',
  PIE: 'PIE',
  GAUGE: 'GAUGE',
  HEATMAP: 'HEATMAP',
  TOP_LIST: 'TOP_LIST',
  TREE_GRID: 'TREE_GRID',
  TABLE: 'TREE_GRID', // TABLE과 TREE_GRID의 매핑 처리
  TOPOLOGY: 'TOPOLOGY',
  EXCLUSIVE: 'EXCLUSIVE',
  STATUS: 'STATUS',
  STATUS_SUMMARY: 'STATUS_SUMMARY',
  SERVER: 'SERVER',
  TEXT: 'TEXT',
  IMAGE: 'IMAGE',
  MEMO: 'MEMO',
  SERVICE_CARD: 'SERVICE_CARD',
  HORIZONTAL_FILTER: 'HORIZONTAL_FILTER',
  VERTICAL_FILTER: 'VERTICAL_FILTER',
  GROUP: 'GROUP',
  TAB_GROUP: 'TAB_GROUP',
  CLUSTER: 'CLUSTER',
  ALERT: 'REALTIME',
  REALTIME: 'REALTIME',
  BY_PERIOD: 'BY_PERIOD',
};

export const displayStyleMap: {
  [key in Exclude<
    WidgetGroupValueResponseV8['displayStyle'],
    undefined
  >]: GridLayoutItem['displayStyle'];
} = {
  LINE: 'LINE',
  BAR: 'BAR',
  AREA: 'AREA',
  STACKED_AREA: 'STACKED_AREA',
  STACKED_BAR: 'STACKED_BAR',
  STYLE_1: 'STYLE_1',
  STYLE_2: 'STYLE_2',
  STYLE_3: 'STYLE_3',
  SINGLE_STACK: 'SINGLE_STACK',
  MULTI_STACK: 'MULTI_STACK',
  TOP_3_CARD: 'TOP_3_CARD',
  SUMMARY_BAR: 'SUMMARY_BAR',
  SUMMARY_CARD: 'SUMMARY_CARD',
  HEXA: 'HEXA',
  CARD: 'CARD',
  PIE: 'PIE',
  TREE_GRID: 'TREE_GRID',
  TABLE: 'TREE_GRID',
  TOPOLOGY: 'TOPOLOGY',
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL',
  SERVER: 'SERVER',
  GAUGE_HALF_CIRCLE: 'GAUGE_HALF_CIRCLE',
  GAUGE_CIRCLE: 'GAUGE_CIRCLE',
  CURRENT_HEATMAP: 'CURRENT_HEATMAP',
  POINT_MAP: 'POINT_MAP',
  CLUSTER: 'CLUSTER',
  ALERT: 'CARD_LIST',
  EQUALIZER_ENTRY: 'EQUALIZER_ENTRY',
  EQUALIZER_GROUP: 'EQUALIZER_GROUP',
  CARD_LIST: 'CARD_LIST',
  SCOREBOARD: 'SCOREBOARD',
  SCOREBOARD_TABLE: 'SCOREBOARD_TABLE',
  ALERT_TABLE: 'ALERT_TABLE',
  SCATTER_BASIC: 'SCATTER_BASIC',
  SCATTER_LONG_TERM: 'SCATTER_LONG_TERM',
};

export const timePeriodMap: {
  [key in Exclude<WidgetGroupValueResponseV8['period'], undefined>]: GridLayoutItem['timePeriod'];
} = {
  globalTime: 'globalTime',
  calendar: 'calendar',
  p5m: 'p5m',
  p10m: 'p10m',
  p30m: 'p30m',
  p1h: 'p1h',
  p6h: 'p6h',
  p12h: 'p12h',
  p1d: 'p1d',
};

export const widgetSeriesTypeMap: {
  [key in Exclude<WidgetValueTargetV7['seriesType'], undefined>]: WidgetChartData['seriesType'];
} = {
  avg: 'avg',
  byTarget: 'byTarget',
  max: 'max',
  sum: 'sum',
};

export const widgetDataTypeMap: {
  [key in Exclude<WidgetValueTargetV7['dataType'], undefined>]: WidgetChartData['dataType'];
} = {
  current: 'current',
  delta: 'delta',
  sum: 'sum',
  avg: 'avg',
  max: 'max',
  deltaPerSec: 'deltaPerSec',
  sumPerSec: 'sumPerSec',
};

export const targetTagTypeMap: { [key in WidgetValueTargetDetailV7['tagType']]: TargetTagType } = {
  attribute: 'attribute',
  globalVariable: 'globalVariable',
  treeGlobalVariable: 'treeGlobalVariable',
  serviceGroup: 'serviceGroup',
  tag: 'tag',
};

export const createTypeMap: {
  [key in Exclude<
    WidgetGroupValueResponseV8['createType'],
    undefined
  >]: GridLayoutItem['createType'];
} = {
  base: 'base',
  custom: 'reference', // custom은 reference로 변환
};

export const createTypeDtoMap: {
  [key in GridLayoutItem['createType']]: WidgetGroupValueResponseV8['createType'];
} = {
  base: 'base',
  reference: 'custom', // reference는 custom으로 변환
};
